import React from 'react';
import { connect } from 'react-redux';
import { injectIntl, defineMessages } from 'react-intl';
import uniqueid from 'lodash.uniqueid';
import Button from '../../../../components/formControls/Button';
import FormSectionTitle from '../../../../components/FormSectionTitle';
import Input from '../../../../components/formControls/Input';
import Select2 from '../../../../components/formControls/Select2';
import ToastMessage from '../../../../components/messages/ToastMessage';
import Icon from '../../../../components/icons/Icon';
import { getVirtualOperatorSettingsFestivities, isSaveFestivitySettingsError, isSaveFestivitySettingsLoaded, isSaveFestivitySettingsSuccess } from '../../../../js/virtualOperator/selectors';
import { saveVirtualOperatorSettingsFestivitiesRequest } from '../../../../js/virtualOperator/actions';

const messages = defineMessages({
  day: {
    id: 'PbxVirtualOperatorSettingsFestivities.day',
    defaultMessage: 'Day',
  },
  month: {
    id: 'PbxVirtualOperatorSettingsFestivities.month',
    defaultMessage: 'Month',
  },
  description: {
    id: 'PbxVirtualOperatorSettingsFestivities.description',
    defaultMessage: 'Description',
  },
  customFestivities: {
    id: 'PbxVirtualOperatorSettingsFestivities.customFestivities',
    defaultMessage: 'Custom festivities',
  },
  buttonSave: {
    id: 'PbxVirtualOperatorSettingsFestivities.button.save',
    defaultMessage: 'Save',
  },
  tooltipRemove: {
    id: 'PbxVirtualOperatorSettingsFestivities.tooltip.remove',
    defaultMessage: 'Remove',
  },
  tooltipAdd: {
    id: 'PbxVirtualOperatorSettingsFestivities.tooltip.add',
    defaultMessage: 'Add',
  },
  errorDate: {
    id: 'PbxVirtualOperatorSettingsFestivities.error.errorDate',
    defaultMessage: 'Please set valid day/month',
  },
  errorDescription: {
    id: 'PbxVirtualOperatorSettingsFestivities.error.errorDescription',
    defaultMessage: 'Please set valid description',
  },
  errorDescriptionDuplicate: {
    id: 'PbxVirtualOperatorSettingsFestivities.error.errorDescriptionDuplicate',
    defaultMessage: 'Description already exists',
  },
  saveError: {
    id: 'PbxVirtualOperatorSettingsFestivities.saveError',
    defaultMessage: 'Saving error',
  },
  saveSuccess: {
    id: 'PbxVirtualOperatorSettingsFestivities.saveSuccess',
    defaultMessage: 'Saving successfully performed',
  },
  info: {
    id: 'PbxVirtualOperatorSettingsFestivities.info',
    defaultMessage:
      'Add here other dates you want to be considered as festivities in Enterprise and Offices sections',
  },
});

const addedEmpty = { date: '', description: '' };

class PbxVirtualOperatorSettingsFestivities extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      added: props.festivities && props.festivities.length ? props.festivities : [addedEmpty],
      errors: props.festivities && props.festivities.length ? props.festivities.map(() => ({})) : [{}],
      formErrors: false,
    };
  }

  onSubmit = (e) => {
    e.preventDefault();
    const {
      save,
      intl: { formatMessage },
    } = this.props;
    const { added } = this.state;
    const errors = {};

    const addedFiltered = added.filter(
      (o) => o.date !== '' || o.description !== ''
    );
    addedFiltered.forEach((o, idx) => {
      const date = o.date.split('/');
      if (date[0] === '' || date[1] === '') {
        errors[idx] = {};
        errors[idx].date = formatMessage(messages.errorDate);
      }
      if (o.description === '') {
        if (!errors[idx]) errors[idx] = {};
        errors[idx].description = formatMessage(messages.errorDescription);
      }
      if (addedFiltered.some((item, idx2) => idx !== idx2 && item.description === o.description)) {
        if (!errors[idx]) errors[idx] = {};
        errors[idx].description = formatMessage(messages.errorDescriptionDuplicate);
      }
    });
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
    } else {
      save({ festivities: addedFiltered });
    }
  };

  produceOptionsDaysMonths = (type) => {
    const options = [];
    const start = type === 'days' ? 1 : 1;
    const limit = type === 'days' ? 31 : 12;
    for (let i = start; i <= limit; i += 1) {
      const value = i < 10 ? `0${i}` : `${i}`;
      options.push({ label: value, value });
    }
    return options;
  };

  handleChangeDescription = (description, idx) => {
    const { added, errors } = this.state;
    if (added.length > 0) {
      added.splice(idx, 1, {
        date: added[idx].date,
        description,
      });
      errors[idx] = {};
    } else {
      added.push({ date: '', description });
    }

    this.setState({
      added,
      errors,
    });
  };

  handleChangeDay = (key, value, idx) => {
    const { added, errors } = this.state;
    const date =
      added[idx] && added[idx].date ? added[idx].date.split('/') : ['', ''];
    if (key === 'days') {
      date[0] = value;
    } else {
      date[1] = value;
    }
    if (added.length > 0) {
      added.splice(idx, 1, {
        date: date.join('/'),
        description: added[idx].description,
      });
      errors[idx] = {};
    } else {
      added.push({ date: date.join('/'), description: '' });
    }
    this.setState({
      added,
      errors,
    });
  };

  handleRemove = (idx) => {
    const { added, errors } = this.state;
    added.splice(idx, 1);
    errors.splice(idx, 1);
    this.setState({
      added,
      errors,
    });
  };

  handleAdd = () => {
    this.setState({ added: [...this.state.added, addedEmpty], errors: [...this.state.errors, {}] });
  };

  render() {
    const {
      saving,
      savingError,
      savingSuccess,
      intl: { formatMessage },
    } = this.props;

    const { added, errors, formErrors } = this.state;
    return (
      <>
        <FormSectionTitle className="mt-3 mb-1">
          {formatMessage(messages.customFestivities)}
        </FormSectionTitle>
        {savingError && (
          <ToastMessage
            type="danger"
            text={formatMessage(messages.saveError)}
            messageCode={savingError}
          />
        )}
        {savingSuccess && (
          <ToastMessage
            type="success"
            text={formatMessage(messages.saveSuccess)}
            messageCode={savingSuccess}
          />
        )}
        {formErrors && (
          <ToastMessage
            type="danger"
            text={formatMessage(messages.errorCheckFields)}
            onClose={() => this.setState({ formErrors: false })}
          />
        )}
        <div className="font-weight-light p-1 pb-3">
          {formatMessage(messages.info)}
        </div>
        <div className="px-2">
          {added.map((f, idx) => {
            const day = f.date !== '' ? f.date.split('/')[0] : '';
            const month = f.date !== '' ? f.date.split('/')[1] : '';
            return (
              <div className="row no-gutters" key={idx}>
                <div className="col-auto">
                  <div className="d-flex">
                    <span style={{ width: '120px' }}>
                      <Select2
                        id={`day_${idx}`}
                        name={`day_${idx}`}
                        value={day ? { label: day, value: day } : null}
                        onChange={(sel) =>
                          this.handleChangeDay('days', sel.value, idx)
                        }
                        options={this.produceOptionsDaysMonths('days')}
                        error={errors[idx] && errors[idx].date ? ' ' : ''}
                        placeholder={formatMessage(messages.day)}
                      />
                    </span>
                    <span style={{ width: '120px' }}>
                      <Select2
                        id={`month_${idx}`}
                        name={`month_${idx}`}
                        value={
                          month ? { label: month, value: month } : null
                        }
                        onChange={(sel) =>
                          this.handleChangeDay('months', sel.value, idx)
                        }
                        options={this.produceOptionsDaysMonths('months')}
                        placeholder={formatMessage(messages.month)}
                        error={errors[idx] && errors[idx].date ? ' ' : ''}
                      />
                    </span>
                  </div>
                  {errors[idx] && errors[idx].date && (
                    <span style={{ fontSize: '90%', color: '#dc3545' }}>
                      {errors[idx].date}
                    </span>
                  )}
                </div>
                <div className="col pl-2">
                  <Input
                    type="text"
                    id={uniqueid('FestivitiesAddedDescription-')}
                    name="FestivitiesAddedDescription"
                    value={f.description}
                    menuPlacement="top"
                    onChange={(e) =>
                      this.handleChangeDescription(e.target.value, idx)
                    }
                    error={errors[idx] && errors[idx].description}
                    placeholder={formatMessage(messages.description)}
                  />
                </div>
                <div className="col-auto pt-2">
                  <>
                    {added && added.length > 0 && (
                      <span
                        className="text-center"
                        onClick={this.handleAdd}
                      >
                        <Icon
                          className="ml-2 text-center"
                          name="add-plus"
                          width={16}
                          height={16}
                          color="var(--yn-blue-dark)"
                          tooltipPosition="right"
                        >
                          {formatMessage(messages.tooltipAdd)}
                        </Icon>
                      </span>
                    )}
                    {added && added.length > 0 && (
                      <span
                        className="text-center"
                        onClick={() => this.handleRemove(idx)}
                      >
                        <Icon
                          className="ml-2 text-center"
                          name="remove-minus"
                          width={16}
                          height={16}
                          color="var(--yn-blue-dark)"
                          tooltipPosition="right"
                        >
                          {formatMessage(messages.tooltipRemove)}
                        </Icon>
                      </span>
                    )}
                  </>
                </div>
              </div>
            );
          })}
        </div>
        <div className="pt-3 text-center">
          <Button
            className="btn btn-primary"
            loading={saving}
            text={formatMessage(messages.buttonSave)}
            onClick={this.onSubmit}
          />
        </div>
      </>
    );
  }
}

function mapStateToProps(state) {
  return {
    saving: !isSaveFestivitySettingsLoaded(state),
    savingError: isSaveFestivitySettingsError(state),
    savingSuccess: isSaveFestivitySettingsSuccess(state),
    festivities: getVirtualOperatorSettingsFestivities(state),
  };
}

export default injectIntl(
  connect(mapStateToProps, {
    save: saveVirtualOperatorSettingsFestivitiesRequest,
  })(PbxVirtualOperatorSettingsFestivities)
);
